import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Zap, Building2, ArrowRight, ChevronRight } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ProductCard from '../components/ProductCard';
import PhoneAuthModal from '../components/PhoneAuthModal';
import B2BApplicationModal from '../components/B2BApplicationModal';
import { productAPI, userAPI } from '../utils/api';

import * as ChannelService from "@channel.io/channel-web-sdk-loader";

ChannelService.loadScript();


function HomePage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [user, setUser] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showB2BModal, setShowB2BModal] = useState(false);

  useEffect(() => {
    loadProducts();
    loadUser();
  }, []);

  const loadUser = async () => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      try {
        const userData = await userAPI.getProfile();
        setUser(userData);
      } catch {
        // 토큰이 유효하지 않으면 무시
      }
    }
  };

  const handleB2BButtonClick = () => {
    if (user) {
      setShowB2BModal(true);
    } else {
      setShowLoginModal(true);
    }
  };

  const handleLoginSuccess = (userData) => {
    setUser(userData);
    setShowLoginModal(false);
    loadProducts();
  };

  const loadProducts = async () => {
    try {
      setLoading(true);
      const data = await productAPI.getList();
      setProducts(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />

      <main className="flex-1">
        {/* 히어로 섹션 */}
        <div className="relative bg-gradient-to-br from-teal-50 via-teal-100/50 to-white overflow-hidden">
          {/* 장식 원형 요소들 */}
          <div className="absolute top-1/2 right-[15%] -translate-y-1/2 w-48 h-48 md:w-64 md:h-64 bg-teal-100/60 rounded-full" />
          <div className="absolute top-[20%] right-[5%] w-32 h-32 md:w-48 md:h-48 bg-teal-200/40 rounded-full" />
          <div className="absolute bottom-[10%] right-[25%] w-24 h-24 md:w-32 md:h-32 bg-teal-800 rounded-full" />

          <div className="container mx-auto px-4 py-16 md:py-24 relative z-10">
            <div className="max-w-xl">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-2 leading-tight">
                고품질 상품을
              </h1>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-teal-500 mb-6 leading-tight">
                합리적인 가격으로
              </h1>
              <p className="text-gray-600 text-base md:text-lg mb-8 leading-relaxed">
                빠른 배송과 합리적인 가격으로<br />
                쇼핑의 즐거움을 느껴보세요
              </p>
              <div className="flex flex-wrap items-center gap-3">
                <Link
                  to="/products"
                  className="inline-flex items-center gap-2 bg-teal-500 text-white px-6 py-3 rounded-lg font-medium hover:bg-teal-600 transition-colors"
                >
                  상품 둘러보기
                </Link>
                <button
                  onClick={() => { ChannelService.openChat(); }}
                  className="inline-flex items-center gap-2 bg-white text-teal-600 px-6 py-3 rounded-lg font-medium border border-teal-200 hover:border-teal-400 hover:bg-teal-50 transition-colors"
                >
                  문의하기
                </button>
              </div>
              <div className="mt-6 flex items-center gap-1.5 text-gray-500 text-sm">
                <Zap className="w-4 h-4 text-teal-500" />
                <span>오늘 주문시 내일 도착 보장</span>
              </div>
            </div>
          </div>
        </div>

        {/* 상품 목록 */}
        <div className="container mx-auto px-4 py-8 md:py-12">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900">추천 상품</h2>
            <p className="text-gray-500 mt-1">
              빠른 배송 가능한 상품은 <Zap className="w-4 h-4 inline text-teal-500" /> 표시가 있습니다
            </p>
          </div>

          {loading && (
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="bg-white rounded-xl border border-gray-200 overflow-hidden animate-pulse">
                  <div className="aspect-square bg-gray-200" />
                  <div className="p-4">
                    <div className="h-5 bg-gray-200 rounded w-3/4 mb-2" />
                    <div className="h-4 bg-gray-200 rounded w-full mb-1" />
                    <div className="h-4 bg-gray-200 rounded w-2/3 mb-3" />
                    <div className="h-6 bg-gray-200 rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {error && (
            <div className="p-4 bg-red-50 rounded-lg text-red-700 border border-red-200">
              {error}
            </div>
          )}

          {!loading && !error && products.length === 0 && (
            <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
              <div className="text-gray-400 mb-4">
                <svg className="w-16 h-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
              </div>
              <p className="text-lg text-gray-600">등록된 상품이 없습니다.</p>
              <p className="text-sm text-gray-400 mt-1">곧 새로운 상품이 등록될 예정입니다.</p>
            </div>
          )}

          {!loading && !error && products.length > 0 && (() => {
            // B2B 전용 상품 제외한 일반 상품
            const normalProducts = products.filter(p => p.product_type !== 'B2B');
            // B2B 전용 상품
            const b2bProducts = products.filter(p => p.product_type === 'B2B');
            // 홈페이지에서는 최대 8개만 표시 (PC 4개x2줄, 모바일 2열x2줄=4개)
            const displayProducts = normalProducts.slice(0, 8);
            const hasMoreProducts = normalProducts.length > 8;

            return (
              <>
                {/* 일반 추천 상품 그리드 - PC 8개, 모바일 4개 */}
                <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-6">
                  {displayProducts.map((product, index) => (
                    <div
                      key={product.uuid}
                      className={index >= 4 ? 'hidden lg:block' : ''}
                    >
                      <ProductCard product={product} />
                    </div>
                  ))}
                </div>

                {/* 상품 더보기 버튼 */}
                {(hasMoreProducts || normalProducts.length > 4) && (
                  <div className="mt-6 text-center">
                    <Link
                      to="/products"
                      className="inline-flex items-center gap-2 px-6 py-3 bg-white border border-gray-200 rounded-full text-gray-700 font-medium hover:border-teal-300 hover:text-teal-600 transition-all group"
                    >
                      상품 더보기
                      <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </div>
                )}

                {/* B2B 회원인 경우: B2B 추천 상품 섹션 */}
                {user?.user_type === 'B2B' && b2bProducts.length > 0 && (
                  <div className="mt-12">
                    <div className="mb-6">
                      <h2 className="text-2xl font-bold text-gray-900">B2B 회원 전용 상품</h2>
                      <p className="text-gray-500 mt-1">
                        특별 할인가로 만나보세요
                      </p>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-6">
                      {b2bProducts.slice(0, 8).map((product, index) => (
                        <div
                          key={product.uuid}
                          className={index >= 4 ? 'hidden sm:block' : ''}
                        >
                          <ProductCard product={product} />
                        </div>
                      ))}
                    </div>
                    {(b2bProducts.length > 8 || b2bProducts.length > 4) && (
                      <div className="mt-6 text-center">
                        <Link
                          to="/products"
                          className="inline-flex items-center gap-2 px-6 py-3 bg-white border border-gray-200 rounded-full text-gray-700 font-medium hover:border-teal-300 hover:text-teal-600 transition-all group"
                        >
                          전체 상품 보기
                          <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                        </Link>
                      </div>
                    )}
                  </div>
                )}

                {/* B2B 회원 신청 CTA 바 - B2B 유저가 아닌 경우에만 표시 */}
                {user?.user_type !== 'B2B' && (
                  <div className="mt-10">
                    <button
                      onClick={handleB2BButtonClick}
                      className="w-full bg-white border border-gray-200 rounded-2xl p-5 sm:p-6 hover:border-purple-300 hover:shadow-md transition-all group"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                            <Building2 className="w-6 h-6 text-purple-600" />
                          </div>
                          <div className="text-left">
                            <h3 className="text-lg sm:text-xl font-bold text-gray-900">B2B 대량 구매 혜택</h3>
                            <p className="text-gray-500 text-sm sm:text-base mt-0.5">
                              최대 <span className="font-semibold text-purple-600">50% 할인</span>된 특별 가격으로 대량 구매가 가능합니다
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 text-purple-600 font-semibold flex-shrink-0">
                          <span className="hidden sm:inline">신청하기</span>
                          <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </div>
                    </button>
                  </div>
                )}
              </>
            );
          })()}
        </div>
      </main>

      <Footer />

      {/* 로그인 모달 */}
      <PhoneAuthModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onSuccess={handleLoginSuccess}
      />

      {/* B2B 신청 모달 */}
      <B2BApplicationModal
        isOpen={showB2BModal}
        onClose={() => setShowB2BModal(false)}
        user={user}
      />
    </div>
  );
}

export default HomePage;
